import { useMemo } from "react";
import { Column, useTable } from "react-table";
import { IconButton, TextField } from "@funcional/react-ui";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { UseFormRegister, useFormContext } from "react-hook-form";
import {
  IDiscount,
  IDiscountRange,
} from "@refund/register-fixed-discount-rules/register/contracts";
import { ReactTable } from "@funcional/react-table-ui";

interface DiscountFormProps {
  register: UseFormRegister<IDiscount>;
  addOption: Function;
  deleteOption: Function;
  data: IDiscountRange[];
  errors: any;
}

interface RangeWithErrors extends IDiscountRange {
  errors?: any;
}

export const DiscountRangesForm = ({
  register,
  addOption,
  deleteOption,
  data,
  errors,
}: DiscountFormProps) => {
  const { getValues, setError, setValue, clearErrors } = useFormContext();
  const preparedData = data.map((range, index) => ({
    ...range,
    errors: errors?.ranges?.[index],
  }));
  // This function is necessary to correct the bug of dont update values properly in collumns using Tables and React-hook-form.
  const updateValue = (event, index, fieldName) => {
    setValue(`ranges.${index}.${fieldName}`, event.target.value);
  };
  const dontAllowRepeatedValues = (fieldName, fieldValues, event) => {
    if (fieldValues.includes(event.target.value)) {
      setError(fieldName, {
        type: "custom",
        message:
          "Não é possível incluir o mesmo desconto do paciente mais de uma vez.",
      });
    } else {
      clearErrors(fieldName);
    }
    return;
  };
  const columns: Column<RangeWithErrors>[] = useMemo(
    () =>
      [
        {
          Header: "Desconto do Paciente",
          accessor: "discount",
          Cell: ({
            row: {
              index,
              original: { errors, id },
            },
            value,
          }: any) => (
            <TextField
              key={id}
              {...register(
                `ranges.${index}.discount` as "ranges.0.discount"
              )}
              type="text"
              defaultValue={value}
              errorMessage={errors?.discount?.message}
              onChange={(event) => {
                let cleanedValue = event.target.value.replace(/[^0-9,.]/g, "").replace(/,/g, ".");
                const decimalCount = cleanedValue.split(".").length - 1;
                if (decimalCount > 1) {
                  cleanedValue = cleanedValue.replace(/\.+$/, "");
                }
                if (cleanedValue !== "" && parseFloat(cleanedValue) > 100.00) {
                  cleanedValue = "100.00";
                }
                event.target.value = cleanedValue;
                const fieldName = `ranges.${index}.discount`;
                const fieldValues = getValues("ranges").map(
                  (range) => range.discount
                );
                dontAllowRepeatedValues(fieldName, fieldValues, event);
                updateValue(event, index, "discount");
              }}
              inputProps={{ style: { minWidth: "2em" }, maxLength: 5 }}
              size="small"
              placeholder="%"
            />
          ),
        },
        {
          Header: "Desconto Fixo",
          accessor: "discountRefund",
          Cell: ({
            row: {
              index,
              original: { errors, id },
            },
            value,
          }: any) => (
            <TextField
              key={id}
              type="text"
              defaultValue={value}
              errorMessage={errors?.discountRefund?.message}
              {...register(
                `ranges.${index}.discountRefund` as "ranges.0.discountRefund"
              )}
              size="small"
              onChange={(event) => {
                let cleanedValue = event.target.value.replace(/[^0-9,.]/g, "").replace(/,/g, ".");
                const decimalCount = cleanedValue.split(".").length - 1;
                if (decimalCount > 1) {
                  cleanedValue = cleanedValue.replace(/\.+$/, "");
                }
                if (cleanedValue !== "" && parseFloat(cleanedValue) > 100.00) {
                  cleanedValue = "100.00";
                }
                event.target.value = cleanedValue;
                updateValue(event, index, "discountRefund");
              }}
              inputProps={{ style: { minWidth: "2em" }, maxLength: 5 }}
              placeholder="%"
            />
          ),
        },
      ] as Column<RangeWithErrors>[],
    [register]
  );
  const tableInstance = useTable(
    { columns, data: preparedData },
    (hooks: any) => {
      hooks.visibleColumns.push((columns: any) => [
        ...columns,
        {
          id: "crud",
          Header: () => (
            <IconButton
              size="small"
              title="Adicionar faixa"
              onClick={addOption}
            >
              <AddIcon />
            </IconButton>
          ),
          Cell: ({ row }: any) => (
            <div className="flex">
              <IconButton
                size="small"
                title="Excluir"
                onClick={() => {
                  deleteOption(row);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ),
        },
      ]);
    }
  );

  return <ReactTable compact footer tableInstance={tableInstance} />;
};
